import type { Me, SetupStartResult, SetupTokenInfo } from '@regia/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { errorMessage, http } from '../../lib/api';
import { keys } from '../../lib/query';
import { Button, Field, Input, Skeleton } from '../../ui';
import { AuthFrame } from './AuthFrame';
import { CodeInput } from './CodeInput';

function readToken(): string {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  return hash.get('token') ?? '';
}

export function SetupPage() {
  const token = useMemo(readToken, []);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const info = useQuery({
    queryKey: ['setup-token', token],
    queryFn: () => http.post<SetupTokenInfo>('/api/auth/setup/check', { token }),
    enabled: token.length > 0,
    retry: false,
  });

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [totp, setTotp] = useState<SetupStartResult | null>(null);
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  if (!token || info.data?.valid === false || info.isError) {
    return (
      <AuthFrame
        title="Link non valido"
        subtitle="Questo link di configurazione non è valido o è scaduto."
      >
        <p className="text-[0.9375rem] text-ink-2">
          Il link vale 24 ore e si usa una volta sola. Fatti mandare un link nuovo e aprilo
          subito.
        </p>
      </AuthFrame>
    );
  }
  if (info.isLoading) {
    return (
      <AuthFrame title="Un momento">
        <Skeleton className="h-40 w-full" />
      </AuthFrame>
    );
  }
  const isReset = info.data?.purpose === 'reset';

  async function start(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = 'Scrivi il tuo nome';
    if (password.length < 10) next.password = 'Almeno 10 caratteri';
    if (password !== password2) next.password2 = 'Le due password non coincidono';
    setErrors(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    try {
      setTotp(
        await http.post<SetupStartResult>('/api/auth/setup/start', {
          token,
          name,
          email,
          password,
        }),
      );
    } catch (err) {
      const fields = (err as { fields?: Record<string, string> }).fields;
      setErrors(fields ?? { form: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  async function finish(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      const me = await http.post<Me>('/api/auth/setup/finish', { token, code, remember: true });
      qc.setQueryData(keys.auth, { needsSetup: false, user: me });
      window.history.replaceState(null, '', '/');
      navigate('/', { replace: true });
    } catch (err) {
      setCode('');
      setErrors({ code: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  if (totp) {
    return (
      <AuthFrame
        title="Proteggi l’accesso"
        subtitle="Inquadra il codice con un’app authenticator (per esempio Google Authenticator o Password di iPhone), poi scrivi il numero che compare."
      >
        <form onSubmit={finish} className="flex flex-col gap-5" noValidate>
          <div className="flex flex-col items-center gap-3 rounded-2xl bg-sheet-2 p-4">
            <img
              src={totp.qrDataUrl}
              alt="Codice QR per l’app authenticator"
              className="h-48 w-48 rounded-lg bg-white p-2"
            />
            <a
              href={totp.otpauthUrl}
              className="text-sm font-semibold text-action underline-offset-2 hover:underline"
            >
              Sei sul telefono? Apri direttamente l’app
            </a>
            <p className="text-center text-xs text-ink-3">
              Oppure inserisci a mano questa chiave:
              <br />
              <span className="tabular break-all text-sm font-semibold text-ink-2 select-all">
                {totp.secret}
              </span>
            </p>
          </div>
          <Field label="Codice a 6 cifre" error={errors.code}>
            {(id, describedBy) => (
              <CodeInput
                id={id}
                value={code}
                onChange={setCode}
                invalid={Boolean(errors.code)}
                describedBy={describedBy}
                autoFocus
              />
            )}
          </Field>
          <Button type="submit" size="lg" block loading={busy} disabled={code.length !== 6}>
            Conferma ed entra
          </Button>
        </form>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame
      title={isReset ? 'Nuovo accesso' : 'Benvenuto in Regia'}
      subtitle={
        isReset
          ? 'Scegli una nuova password e collega di nuovo l’app authenticator.'
          : 'Crea il tuo accesso. Servono una password e l’app authenticator sul telefono.'
      }
    >
      <form onSubmit={start} className="flex flex-col gap-4" noValidate>
        <Field label="Il tuo nome" error={errors.name}>
          {(id) => (
            <Input
              id={id}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              invalid={Boolean(errors.name)}
            />
          )}
        </Field>
        <Field
          label="Email"
          error={errors.email}
          hint={isReset && info.data?.email ? `Usa ${info.data.email}` : undefined}
        >
          {(id, describedBy) => (
            <Input
              id={id}
              type="email"
              inputMode="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              invalid={Boolean(errors.email)}
              aria-describedby={describedBy}
            />
          )}
        </Field>
        <Field label="Password" error={errors.password} hint="Almeno 10 caratteri">
          {(id, describedBy) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              invalid={Boolean(errors.password)}
              aria-describedby={describedBy}
            />
          )}
        </Field>
        <Field label="Ripeti la password" error={errors.password2}>
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="new-password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              invalid={Boolean(errors.password2)}
            />
          )}
        </Field>
        {errors.form ? <p className="text-sm text-alert">{errors.form}</p> : null}
        <Button type="submit" size="lg" block loading={busy} className="mt-2">
          Continua
        </Button>
      </form>
    </AuthFrame>
  );
}
